/**
 * Image sizing utilities for responsive chat message images
 */
import { cn } from './index';

/**
 * Get responsive width classes for chat message containers with images
 */
export function getChatMessageWithImagesWidthClasses(): string {
  return 'max-w-[95%] sm:max-w-[90%] md:max-w-[85%] lg:max-w-[80%]'; // Wider for images
}

/**
 * Get responsive width classes for text-only chat message containers
 */
export function getChatMessageTextOnlyWidthClasses(): string {
  return 'max-w-[85%] sm:max-w-[75%] md:max-w-[65%] lg:max-w-[55%]'; // Standard for text
}

/**
 * Get responsive size classes for individual images when image viewer is half width
 */
export function getImageSizeClassesHalfWidth(): string {
  return cn(
    "overflow-hidden rounded-lg aspect-square flex-shrink-0",
    "w-[calc(25%-0.375rem)] max-w-[160px] min-w-[100px]" // Smaller when image viewer is open
  );
}

/**
 * Get responsive size classes for individual images when image viewer is full width
 */
export function getImageSizeClassesFullWidth(): string {
  return cn(
    "overflow-hidden rounded-lg aspect-square flex-shrink-0",
    "w-[calc(25%-0.375rem)] max-w-[218px] min-w-[120px]" // Original size when full width
  );
}

/**
 * Get container classes for image grids
 */
export function getImageGridClasses(): string {
  return "flex flex-wrap gap-2 max-w-4xl";
}

/**
 * Get optimal layout classes for single image in half width mode
 */
export function getSingleImageLayoutHalfWidth(): string {
  return "w-[calc(50%-0.25rem)] max-w-[240px]";
}

/**
 * Get optimal layout classes for single image in full width mode
 */
export function getSingleImageLayoutFullWidth(): string {
  return "w-[calc(50%-0.25rem)] max-w-[320px]";
}

/**
 * Get optimal layout classes for two images in half width mode
 */
export function getTwoImagesLayoutHalfWidth(): string {
  return "w-[calc(50%-0.25rem)] max-w-[160px]";
}

/**
 * Get optimal layout classes for two images in full width mode
 */
export function getTwoImagesLayoutFullWidth(): string {
  return "w-[calc(50%-0.25rem)] max-w-[218px]";
}

/**
 * Get optimal layout classes for three images in half width mode
 */
export function getThreeImagesLayoutHalfWidth(): string {
  return "w-[calc(33.333%-0.375rem)] max-w-[140px]";
}

/**
 * Get optimal layout classes for three images in full width mode
 */
export function getThreeImagesLayoutFullWidth(): string {
  return "w-[calc(33.333%-0.375rem)] max-w-[200px]";
}

/**
 * Get optimal layout classes for four or more images in half width mode
 */
export function getFourOrMoreImagesLayoutHalfWidth(): string {
  return "w-[calc(25%-0.375rem)] max-w-[120px]";
}

/**
 * Get optimal layout classes for four or more images in full width mode
 */
export function getFourOrMoreImagesLayoutFullWidth(): string {
  return "w-[calc(25%-0.375rem)] max-w-[180px]";
}
